import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Divider from '@material-ui/core/Divider';
import classNames from 'classnames';
import Button from 'Components/UI/Button';
import SyncIcon from '@material-ui/icons/Sync';
import { fetchFromURLParamsFromWebhook } from 'Components/AdvisorApp/Viewport/ApexAdminPortal/ApexAdminPortalActions';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { displaySuccess } from 'Helpers/FacetNotificationManager';
import useUserContext from 'Context/UserContext';
import {
  useApexAdminPortalDispatchContext,
  useApexAdminPortalStateContext,
} from 'Components/AdvisorApp/Viewport/ApexAdminPortal/ApexAdminPortalContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import OpenAccountButtonPicker
  from 'Components/AdvisorApp/Viewport/ApexAdminPortal/OpenAccountButtonPicker/OpenAccountButtonPicker';
import AccountDetails from './AccountDetails';
import AccountInfo from './AccountInfo';
import AccountsTable from './AccountsTable';
import AccountRequestFiltering from './AccountRequestFiltering';

const styles = theme => ({
  root: {
    display: 'flex',
    flexFlow: 'column',
    flex: 1,
  },
  accountFormsWrapper: {
    display: 'flex',
    flexFlow: 'row',
    flex: 1,
  },
  left: {
    flexBasis: '10em',
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  center: {
    flexGrow: 1,
    flexShrink: 0,
    transition: theme.transitions.create('flex-basis', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
  },
  centerExpanded: {
    flexBasis: '20em',
  },
  centerCompressed: {
    flexBasis: '3em',
  },
  right: {
    flexBasis: '25em',
    flexGrow: 0,
    flexShrink: 0,
  },
  rightOptional: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: '30em',
  },
  divider: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  openAccountButton: {
    marginTop: theme.spacing(2),
  },
  headerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    position: 'sticky',
    top: theme.heights.advisorNavigationHeight,
    background: theme.palette.common.white,
    zIndex: 100,
  },
  mainHeaderTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  refreshButton: {
    marginRight: theme.spacing(1),
  },
});

const ApexAdminPortal = ({
  classes,
  isCenterCompressed,
  amplitudeRootNodeProps,
}) => {
  const { userResource } = useUserContext();
  const apexAdminPortalDispatch = useApexAdminPortalDispatchContext();
  const { refreshLink: contextRefreshLink } = useApexAdminPortalStateContext();
  const { logEvent } = useEventTrackingLogEvent({ nodeName: 'Account Left Panel' });
  const refreshFromWebhook = async () => {
    await fetchFromURLParamsFromWebhook(userResource, contextRefreshLink,
      apexAdminPortalDispatch, () => {
        displaySuccess('Refreshed All');
      });
  };
  return (
    <div className={classes.root} {...amplitudeRootNodeProps}>
      <div className={classes.headerWrapper}>
        <Typography
          className={classes.mainHeaderTitle}
          variant="h2"
          color="primary"
        >
          {'Account Opening Requests'}
        </Typography>
        <div className={classes.actionButtons}>
          <Button
            className={classes.refreshButton}
            onClick={() => {
              refreshFromWebhook();
              logEvent('Clicked Refresh All Button');
            }}
            variant="text"
            color="secondary"
          >
            <SyncIcon className={classes.refreshIcon} />
                        Refresh All
          </Button>
          <OpenAccountButtonPicker />
        </div>
      </div>
      <Box className={classes.accountFormsWrapper}>
        <Box className={classes.left} debugColor="dodgerBlue">
          <AccountRequestFiltering />
        </Box>
        <Divider orientation="vertical" flexItem className={classes.divider} />
        <Box
          debugColor="crimson"
          className={classNames(classes.center, {
            [classes.centerExpanded]: !isCenterCompressed,
            [classes.centerCompressed]: isCenterCompressed,
          })}
        >
          <AccountsTable
            isCenterCompressed={isCenterCompressed}
          />
        </Box>
        <Divider orientation="vertical" flexItem className={classes.divider} />
        <Box className={classes.right} debugColor="gold">
          <AccountInfo />
        </Box>
        {isCenterCompressed && (
        <Divider
          orientation="vertical"
          flexItem
          className={classes.divider}
        />
        )}
        {isCenterCompressed && (
        <Box className={classes.rightOptional} debugColor="gold">
          <AccountDetails />
        </Box>
        )}
      </Box>
    </div>
  );
};

ApexAdminPortal.propTypes = {
  classes: PropTypes.object.isRequired,
  isCenterCompressed: PropTypes.bool,
  amplitudeRootNodeProps: PropTypes.object.isRequired,
};
ApexAdminPortal.defaultProps = {
  isCenterCompressed: false,
};

export default withStyles(styles)(ApexAdminPortal);
